import { AnyAction } from 'redux';
import * as actionTypes from '../../../../models/actionTypes';
import {
  initialRequestDetailsState,
  IRequestDetailsState,
} from '../../../../models/reduxState';
import { deepCopyProperty } from '../../../../utilities';
import { Note, AttachmentData } from '../../models/requestModels';
import { updateDraftRequest } from './modalitySectionReducer';

const handleDataGrabSuccess = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  return {
    ...state,
    isRequestEditingEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsRequestEditingEnabled,
    addNoteConfig: !!action.supportSettingsConfig ? action.supportSettingsConfig.AddNoteConfig : null,
    isC2CPhoneEditingEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsC2CPhoneEditingEnabled,
    isRequestDetailsPhoneEditEnabled: !!action.supportSettingsConfig && !!action.supportSettingsConfig.IsRequestDetailsPhoneEditEnabled,
  };
};

const handleSetDirtyRequest = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  const requestDetailsState = { ...state };
  const request = action.request;
  if (request) {
    requestDetailsState.dirtyRequest = request;
    const userPhone = request.UserPhone || '';
    if (!requestDetailsState.dirtyRequest.UserPhoneWithoutDialingCode) {
      requestDetailsState.dirtyRequest.UserPhoneWithoutDialingCode =
        userPhone.indexOf('-') !== -1 ? userPhone.split('-')[1] : userPhone;
    }
    if (!requestDetailsState.dirtyRequest.UserCountryDialingCode) {
      requestDetailsState.dirtyRequest.UserCountryDialingCode =
        userPhone.indexOf('-') !== -1 ? userPhone.split('-')[0] : '';
    }
    requestDetailsState.isEmailValid = true;
    requestDetailsState.isPhoneNumberValid = true;
    //  make a deep copy of notes and push Empty Note Placeholder to dirtyRequest.
    const notes = !!request.Notes ? deepCopyProperty(request.Notes): [];
    const addNoteConfig = requestDetailsState.addNoteConfig;
    const maxAllowedNotesCount = addNoteConfig? addNoteConfig.MaxAllowedNotesCount : 6;
    if (!!notes && notes.length <= maxAllowedNotesCount) {
      // Add an empty note here to track new note change.
      const newNote = new Note();
      notes.push(newNote);
    }
    requestDetailsState.dirtyRequest.Notes = notes;
  } else {
    requestDetailsState.dirtyRequest = null;
  }
  return requestDetailsState;
};

const handleDirtyRequestUpdate = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  const requestDetailsState = { ...state };
  const request = action.request;
  if (request) {
    requestDetailsState.dirtyRequest = updateDraftRequest(
      requestDetailsState.dirtyRequest,
      request
    );
  }
  return requestDetailsState;
};

const handleAddAttachment = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  if (!!action.requestId) {
    const request = { ...state.dirtyRequest };
    const attachments = request.Attachments ? request.Attachments: [];
    const newAttachmentData = new AttachmentData();
    newAttachmentData.Name = action.name;
    // Need to add a empty string to make up the cswId since it's needed by the component's key
    newAttachmentData.CwsId = '';
    attachments.push(newAttachmentData);
    // In action GET_ATTACHMENT_UPLOAD_TOKEN_ERROR, UPLOAD_FILE_METADATA_ERROR, and UPLOAD_FILE_CHUNK_ERROR, the last attachmentData will be pop() since the upload fails.
    // In action UPLOAD_FILE_METADATA_SUCCESS, the last attachmentdata will be pop() to update.
    return {
      ...state,
      dirtyRequest: updateDraftRequest(state.dirtyRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleDeleteAttachment = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  if (!!action.requestId) {
    const request = { ...state.dirtyRequest };
    const attachments = request.Attachments;
    const payloadCwsId: string = action.cwsId;
    const cwsId = payloadCwsId.toLowerCase();
    if (!payloadCwsId) {
      return state;
    }
    let removeIndex = -1;
    !!attachments && attachments.forEach((attachment: AttachmentData, index: number) => {
      if (!!attachment.CwsId && attachment.CwsId.toLowerCase() === cwsId) {
        removeIndex = index;
      }
    });
    if (removeIndex > -1) {
      attachments.splice(removeIndex, 1);
    }
    return {
      ...state,
      dirtyRequest: updateDraftRequest(state.dirtyRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleCancelUploadAttachment = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  if (!!action.requestId) {
    const request = { ...state.dirtyRequest };
    const attachments = request.Attachments ? request.Attachments: [];
    // Need to be removed from the attachments array if the uploading process is canceled.
    if (!!attachments) {
      attachments.pop();
    }
    return {
      ...state,
      dirtyRequest: updateDraftRequest(state.dirtyRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleGetUploadAttachmentTokenError = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  if (!!action.requestId) {
    const request = { ...state.dirtyRequest };
    const attachments = request.Attachments ? request.Attachments: [];
    // AttachmentData with Name and cwsId is added when addAttachmentAction is trigged, should be pop() if getUploadAttachmentToken api failed.
    if (!!attachments) {
      attachments.pop();
    }
    return {
      ...state,
      dirtyRequest: updateDraftRequest(state.dirtyRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleUploadFileMetadataError = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  if (!!action.requestId) {
    const request = { ...state.dirtyRequest };
    const attachments = request.Attachments ? request.Attachments: [];
    // AttachmentData with Name and cwsId is added when addAttachmentAction is trigged, should be pop() if the uploadFileMetadata api failed.
    if (!!attachments) {
      attachments.pop();
    }
    return {
      ...state,
      dirtyRequest: updateDraftRequest(state.dirtyRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleUploadFileChunkError = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  if (!!action.requestId) {
    const request = { ...state.dirtyRequest };
    const attachments = request.Attachments ? request.Attachments: [];
    //  AttachmentData with Name and cwsId is added when addAttachmentAction is trigged, should be pop() if the uploadFileChunk api failed.
    if (!!attachments) {
      attachments.pop();
    }
    return {
      ...state,
      dirtyRequest: updateDraftRequest(state.dirtyRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleUploadAttachmentSuccess = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  if (!!action.requestId) {
    const request = { ...state.dirtyRequest };
    const attachments = request.Attachments ? request.Attachments: [];
    // AttachmentData with Name and cwsId is added when addAttachmentAction is trigged, should be pop() first to update.
    if (!!attachments) {
      attachments.pop();
    }
    attachments.push(action.attachmentData);
    return {
      ...state,
      dirtyRequest: updateDraftRequest(state.dirtyRequest, { Attachments: attachments }),
    };
  } else {
    return state;
  }
};

const handleUpdateAttachments = (
  state: IRequestDetailsState = initialRequestDetailsState
): IRequestDetailsState => {
  return {
    ...state,
    isUpdatingAttachments: true,
    isUpdateAttachmentsError: false,
  };
};

const handleUpdateAttachmentsSuccess = (
  state: IRequestDetailsState = initialRequestDetailsState
): IRequestDetailsState => {
  return {
    ...state,
    isUpdatingAttachments: false,
    isUpdateAttachmentsError: false,
  };
};

const handleUpdateAttachmentsError = (
  state: IRequestDetailsState = initialRequestDetailsState
): IRequestDetailsState => {
  return {
    ...state,
    isUpdatingAttachments: false,
    isUpdateAttachmentsError: true,
  };
};

export const handleUpdateNote = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  const notes = state.dirtyRequest ? state.dirtyRequest.Notes : [];
  if (!!notes && notes.length > 1) {
    const note = action.note;
    const len = notes.length;
    const newNote = new Note();
    newNote.Text = note;
    notes[len-1] = newNote;
  }
  return {
    ...state,
    dirtyRequest: updateDraftRequest(state.dirtyRequest, { Notes: notes }),
  };
};

const handleAddNote = (
  state: IRequestDetailsState = initialRequestDetailsState
): IRequestDetailsState => {
  return {
    ...state,
    isAddingNote: true,
    isAddNoteError: false,
  };
};

const handleAddNoteSuccess = (
  state: IRequestDetailsState = initialRequestDetailsState
): IRequestDetailsState => {
  return {
    ...state,
    isAddingNote: false,
    isAddNoteError: false,
  };
};

const handleAddNoteError = (
  state: IRequestDetailsState = initialRequestDetailsState
): IRequestDetailsState => {
  const notes = state.dirtyRequest ? state.dirtyRequest.Notes : [];
  return {
    ...state,
    isAddingNote: false,
    isAddNoteError: true,
    dirtyRequest: updateDraftRequest(state.dirtyRequest, { Notes: notes }),
  };
};

const handleIsEmailValidUpdate = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  return {
    ...state,
    isEmailValid: action.isEmailValid,
  };
};

const handleIsPhoneValidUpdate = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  return {
    ...state,
    isPhoneNumberValid: action.isPhoneNumberValid,
  };
};

const handleUpdateContacts = (
  state: IRequestDetailsState = initialRequestDetailsState
): IRequestDetailsState => {
  return {
    ...state,
    isUpdatingContacts: true,
    isUpdateContactsError: false,
  };
};

const handleUpdateContactsError = (
  state: IRequestDetailsState = initialRequestDetailsState
): IRequestDetailsState => {
  return {
    ...state,
    isUpdatingContacts: false,
    isUpdateContactsError: true,
  };
};

const handleUpdateContactsSuccess = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  const response = action.response;
  if (!!response) {
    return {
      ...state,
      isUpdatingContacts: false,
      isUpdateContactsError: !response.IsUpdateExtensionAttributesSuccessful,
    };
  }
  return state;
};

const handleResetRequestDetailsEditError = (
  state: IRequestDetailsState = initialRequestDetailsState
): IRequestDetailsState => {
  return {
    ...state,
    isUpdateAttachmentsError: false,
    isAddNoteError: false,
    isUpdateContactsError: false,
  };
};

const handleUpdateBusinessImpactConfirmed = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  return {
    ...state,
    businessImpactConfirmed: action.businessImpactConfirmed,
  };
};

const handleUpdateCustomerAvailabilityConfirmed = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  return {
    ...state,
    customerAvailabilityConfirmed: action.customerAvailabilityConfirmed,
  };
};

const handleGetRequestUpdate = (
  state: IRequestDetailsState = initialRequestDetailsState,
  isPending: boolean
): IRequestDetailsState => {
  return {
    ...state,
    isGetRequestPending: isPending,
  };
};

export const RequestDetailsReducer = (
  state: IRequestDetailsState = initialRequestDetailsState,
  action: AnyAction
): IRequestDetailsState => {
  switch (action.type) {
    case actionTypes.GET_EMERALD_SUPPORTCONFIG_SUCCESS:
    case actionTypes.GET_EMERALD_CONFIG_SUCCESS:
      return handleDataGrabSuccess(state, action);
    case actionTypes.SET_DIRTY_REQUEST:
      return handleSetDirtyRequest(state, action);
    case actionTypes.UPDATE_DIRTY_REQUEST:
      return handleDirtyRequestUpdate(state, action);
    case actionTypes.UPDATE_IS_PHONENUMBER_VALID_FOR_EDIT:
      return handleIsPhoneValidUpdate(state, action);
    case actionTypes.UPDATE_IS_EMAIL_VALID_FOR_EDIT:
      return handleIsEmailValidUpdate(state, action);
    case actionTypes.ADD_ATTACHMENT:
      return handleAddAttachment(state, action);
    case actionTypes.DELETE_ATTACHMENT:
      return handleDeleteAttachment(state, action);
    case actionTypes.CANCEL_UPLOAD_ATTACHMENT:
      return handleCancelUploadAttachment(state, action);
    case actionTypes.GET_ATTACHMENT_UPLOAD_TOKEN_ERROR:
      return handleGetUploadAttachmentTokenError(state, action);
    case actionTypes.UPLOAD_FILE_METADATA_ERROR:
      return handleUploadFileMetadataError(state, action);
    case actionTypes.UPLOAD_FILE_CHUNK_ERROR:
      return handleUploadFileChunkError(state, action);
    case actionTypes.UPLOAD_ATTACHMENT_SUCCESS:
      return handleUploadAttachmentSuccess(state, action);
    case actionTypes.UPDATE_ATTACHMENTS:
      return handleUpdateAttachments(state);
    case actionTypes.UPDATE_ATTACHMENTS_ERROR:
      return handleUpdateAttachmentsError(state);
    case actionTypes.UPDATE_ATTACHMENTS_SUCCESS:
      return handleUpdateAttachmentsSuccess(state);
    case actionTypes.UPDATE_NOTE:
      return handleUpdateNote(state, action);
    case actionTypes.ADD_NOTE:
      return handleAddNote(state);
    case actionTypes.ADD_NOTE_SUCCESS:
      return handleAddNoteSuccess(state);
    case actionTypes.ADD_NOTE_ERROR:
      return handleAddNoteError(state);
    case actionTypes.UPDATE_CONTACTS:
      return handleUpdateContacts(state);
    case actionTypes.UPDATE_CONTACTS_ERROR:
      return handleUpdateContactsError(state);
    case actionTypes.UPDATE_CONTACTS_SUCCESS:
      return handleUpdateContactsSuccess(state, action);
    case actionTypes.RESET_REQUEST_DETAILS_EDIT_ERROR:
      return handleResetRequestDetailsEditError(state);
    case actionTypes.UPDATE_BUSINESS_IMPACT_CONFIRMED_FOR_EDIT:
      return handleUpdateBusinessImpactConfirmed(state, action);
    case actionTypes.UPDATE_CUSTOMER_AVAILABILITY_CONFIRMED_FOR_EDIT:
      return handleUpdateCustomerAvailabilityConfirmed(state, action);
    case actionTypes.GET_REQUEST:
      return handleGetRequestUpdate(state, true);
    case actionTypes.GET_REQUEST_SUCCESS:
    case actionTypes.GET_REQUEST_ERROR:
      return handleGetRequestUpdate(state, false);
    default:
      return state;
  }
};
